import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Check, Sparkles, Crown, Zap, ShieldCheck,
  Loader2, CreditCard, Building2, MapPin, FileText, Boxes, FilePlus,
  ArrowRight, Minus, Plus,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "plans",        labelPt: "Planos",          labelEn: "Plans" },
  { id: "addons",       labelPt: "Add-ons",         labelEn: "Add-ons" },
  { id: "billing",      labelPt: "Detalhes",        labelEn: "Billing details" },
  { id: "confirmation", labelPt: "Confirmação",     labelEn: "Confirmation" },
];

const PLAN_ICONS = { growth: Zap, premium: Crown, ultimate: ShieldCheck };
const ADDON_ICONS = { leadbooster: Sparkles, projects: Boxes, smart_docs: FilePlus };

function COPY(lang) {
  const isPt = lang === "pt";
  return {
    title: isPt ? "Cobrança & Assinatura" : "Billing & Subscription",
    subtitle: isPt
      ? "Gerencie seu plano, add-ons e detalhes de cobrança."
      : "Manage your plan, add-ons and billing details.",
    breadcrumb: { settings: isPt ? "Configurações" : "Settings", billing: isPt ? "Cobrança" : "Billing" },
    back: isPt ? "Voltar" : "Back",
    teamSize: isPt ? "Tamanho da equipe" : "Team size",
    seats: (n) => (isPt ? `${n} ${n === 1 ? "assento" : "assentos"}` : `${n} ${n === 1 ? "seat" : "seats"}`),
    monthly: isPt ? "Mensal" : "Pay monthly",
    yearly: isPt ? "Anual" : "Pay yearly",
    save25: isPt ? "Economize 25%" : "Save 25%",
    perSeat: isPt ? "/ assento / mês" : "/ seat / month",
    perDay: (v) => (isPt ? `≈ $${v} por dia` : `≈ $${v} per day`),
    chooseplan: isPt ? "Escolher plano" : "Choose plan",
    currentPlan: isPt ? "Plano atual" : "Current plan",
    addons: isPt ? "Add-ons" : "Add-ons",
    orderSummary: isPt ? "Resumo do pedido" : "Order summary",
    trial14: isPt ? "Teste grátis de 14 dias" : "14-day free trial",
    yourPlan: isPt ? "Seu plano" : "Your plan",
    billingCycle: isPt ? "Ciclo de cobrança" : "Billing cycle",
    seatsLabel: isPt ? "Assentos" : "Seats",
    total: isPt ? "Total" : "Total",
    perMonth: isPt ? "/ mês" : "/ month",
    perYear: isPt ? "/ ano" : "/ year",
    continueTrial: isPt ? "Continuar trial" : "Continue trial",
    continue: isPt ? "Continuar" : "Continue",
    contactInfo: isPt ? "Informações de contato" : "Contact info",
    fullName: isPt ? "Nome completo" : "Full name",
    company: isPt ? "Empresa" : "Company",
    email: isPt ? "E-mail" : "Email",
    address: isPt ? "Endereço de cobrança" : "Billing address",
    country: isPt ? "País" : "Country",
    city: isPt ? "Cidade" : "City",
    payment: isPt ? "Pagamento" : "Payment",
    cardNumber: isPt ? "Número do cartão" : "Card number",
    exp: isPt ? "Validade" : "Expiry",
    cvc: "CVC",
    confirmTitle: isPt ? "Tudo pronto" : "All set",
    confirmDesc: isPt
      ? "Revise os detalhes antes de confirmar a alteração de plano."
      : "Review the details before confirming your plan change.",
    confirmCta: isPt ? "Confirmar e ativar" : "Confirm and activate",
    next: isPt ? "Próximo" : "Next",
    prev: isPt ? "Voltar" : "Back",
    activated: isPt ? "Plano ativado com sucesso!" : "Plan activated successfully!",
    failed: isPt ? "Falha ao processar" : "Process failed",
  };
}

function formatPrice(n) {
  return Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export default function Billing() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const c = useMemo(() => COPY(lang), [lang]);

  const [tab, setTab] = useState("plans");
  const [catalog, setCatalog] = useState({ plans: [], addons: [] });
  const [seats, setSeats] = useState(1);
  const [cycle, setCycle] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [selectedAddons, setSelectedAddons] = useState(["leadbooster", "projects", "smart_docs"]);
  const [subscription, setSubscription] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    full_name: "", company: "", email: "", address: "", country: "", city: "",
    card_number: "", exp: "", cvc: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [cat, sub] = await Promise.all([
          axios.get(`${API}/billing/catalog`),
          axios.get(`${API}/billing/subscription`),
        ]);
        setCatalog(cat.data || { plans: [], addons: [] });
        if (sub.data) {
          setSubscription(sub.data);
          if (sub.data.plan_id) setSelectedPlan(sub.data.plan_id);
          if (sub.data.billing_cycle) setCycle(sub.data.billing_cycle);
          if (sub.data.seats) setSeats(sub.data.seats);
          if (Array.isArray(sub.data.addons)) setSelectedAddons(sub.data.addons);
        }
      } catch (e) { console.error("billing load", e); }
    };
    load();
  }, []);

  const plan = useMemo(
    () => catalog.plans.find((p) => p.id === selectedPlan),
    [catalog.plans, selectedPlan]
  );

  const monthlyPrice = plan ? plan.monthly : 0;
  const yearlyPriceTotal = plan ? Math.round(plan.monthly * 12 * 0.75) : 0;
  const isYearly = cycle === "yearly";
  const unit = isYearly ? Math.round(yearlyPriceTotal / 12) : monthlyPrice;
  const total = unit * seats;
  const dailyApprox = (unit / 30).toFixed(2);

  const isCurrentPlan = (planId) => subscription?.plan_id === planId && subscription?.status !== "trial";

  const toggleAddon = (id) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const goNext = () => {
    const idx = TABS.findIndex((t) => t.id === tab);
    if (idx < TABS.length - 1) setTab(TABS[idx + 1].id);
  };
  const goPrev = () => {
    const idx = TABS.findIndex((t) => t.id === tab);
    if (idx > 0) setTab(TABS[idx - 1].id);
  };

  const submitCheckout = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: selectedPlan,
        billing_cycle: cycle,
        seats,
        addons: selectedAddons,
      });
      if (res.data?.ok) {
        toast.success(c.activated);
        setLastInvoice(res.data.invoice || null);
        const fresh = await axios.get(`${API}/billing/subscription`);
        setSubscription(fresh.data);
        setTab("confirmation");
      }
    } catch (_e) {
      toast.error(c.failed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="billing-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="billing-back"
            className="inline-flex items-center gap-1.5 text-[13px] text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
          >
            <ArrowLeft className="w-4 h-4" /> {c.back}
          </button>
          <div className="text-[12px] text-[var(--al-text-muted)] flex items-center gap-1.5 ml-2">
            <span>{c.breadcrumb.settings}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[var(--al-text)]">{c.breadcrumb.billing}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <LangToggle />
          </div>
        </header>

        <main className="px-8 py-8 max-w-[1400px] mx-auto">
          <div className="mb-8">
            <h1 className="text-[28px] font-bold tracking-tight text-[var(--al-text)]">{c.title}</h1>
            <p className="text-[14px] text-[var(--al-text-muted)] mt-1">{c.subtitle}</p>
          </div>

          {/* Tabs */}
          <nav className="border-b border-[var(--al-border)] flex items-center gap-1 mb-8">
            {TABS.map((tb, idx) => (
              <button
                key={tb.id}
                type="button"
                data-testid={`billing-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`relative px-5 py-3 text-[13.5px] font-medium transition-colors ${
                  tab === tb.id
                    ? "text-[#1B3A8A] border-b-2 border-[#C9A24E] -mb-px"
                    : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
                }`}
              >
                <span className="text-[10px] mr-2 inline-flex w-5 h-5 items-center justify-center rounded-full bg-[var(--al-row-alt)] text-[var(--al-text-muted)]">
                  {idx + 1}
                </span>
                {lang === "pt" ? tb.labelPt : tb.labelEn}
              </button>
            ))}
          </nav>

          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <section className="col-span-12 lg:col-span-8 space-y-6">
              {tab === "plans" && (
                <PlansTab
                  c={c} catalog={catalog} lang={lang}
                  seats={seats} setSeats={setSeats}
                  cycle={cycle} setCycle={setCycle}
                  selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan}
                  isCurrentPlan={isCurrentPlan} dailyApprox={dailyApprox}
                />
              )}
              {tab === "addons" && (
                <AddonsTab
                  c={c} addons={catalog.addons} lang={lang}
                  selectedAddons={selectedAddons} toggleAddon={toggleAddon}
                />
              )}
              {tab === "billing" && (
                <BillingDetailsTab c={c} value={billingDetails} onChange={setBillingDetails} />
              )}
              {tab === "confirmation" && (
                <ConfirmationTab
                  c={c} plan={plan} cycle={cycle} seats={seats}
                  addons={catalog.addons.filter((a) => selectedAddons.includes(a.id))}
                  total={total} isYearly={isYearly}
                  onSubmit={submitCheckout} submitting={submitting}
                  subscription={subscription}
                  lastInvoice={lastInvoice}
                />
              )}

              <div className="flex items-center justify-between pt-4">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={tab === "plans"}
                  data-testid="billing-prev"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[var(--al-border)] text-[13px] text-[var(--al-text)] hover:bg-[var(--al-row-alt)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" /> {c.prev}
                </button>
                {tab !== "confirmation" && (
                  <button
                    type="button"
                    onClick={goNext}
                    data-testid="billing-next"
                    className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[13px] font-semibold"
                  >
                    {c.next} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </section>

            <aside className="col-span-12 lg:col-span-4">
              <OrderSummary
                c={c} plan={plan} addons={catalog.addons.filter((a) => selectedAddons.includes(a.id))}
                seats={seats} cycle={cycle} unit={unit} total={total}
                lang={lang} subscription={subscription}
                onContinueTrial={() => toast.info(lang === "pt" ? "Trial mantido — sem cobrança." : "Trial kept — no charge.")}
                onContinue={submitCheckout} submitting={submitting}
              />
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}

// ----- Sub-components --------------------------------------------------------

function PlansTab({ c, catalog, lang, seats, setSeats, cycle, setCycle, selectedPlan, setSelectedPlan, isCurrentPlan, dailyApprox }) {
  return (
    <div className="space-y-6">
      {/* Team size + cycle */}
      <div className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-xl p-5 lg:p-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-2">
            {c.teamSize}
          </div>
          <div
            data-testid="billing-seats-stepper"
            className="inline-flex items-center bg-[var(--al-row-alt)] border border-[var(--al-border)] rounded-lg overflow-hidden"
          >
            <button
              type="button" data-testid="billing-seats-dec"
              onClick={() => setSeats(Math.max(1, seats - 1))}
              className="w-9 h-9 flex items-center justify-center text-[var(--al-text-muted)] hover:bg-[var(--al-card-bg)]"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <input
              data-testid="billing-seats-input"
              type="number" min={1} max={500} value={seats}
              onChange={(e) => setSeats(Math.max(1, Math.min(500, Number(e.target.value || 1))))}
              className="w-14 h-9 bg-transparent text-center text-[14px] font-semibold outline-none"
            />
            <button
              type="button" data-testid="billing-seats-inc"
              onClick={() => setSeats(Math.min(500, seats + 1))}
              className="w-9 h-9 flex items-center justify-center text-[var(--al-text-muted)] hover:bg-[var(--al-card-bg)]"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-[11px] text-[var(--al-text-muted)] mt-1">{c.seats(seats)}</div>
        </div>

        <div className="md:ml-auto">
          <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-2 md:text-right">
            {c.billingCycle}
          </div>
          <div className="inline-flex bg-[var(--al-row-alt)] rounded-lg p-1 border border-[var(--al-border)]">
            {["monthly", "yearly"].map((opt) => {
              const active = cycle === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  data-testid={`billing-cycle-${opt}`}
                  onClick={() => setCycle(opt)}
                  className={`px-4 h-8 text-[12.5px] font-medium rounded-md transition-all ${
                    active
                      ? "bg-[var(--al-card-bg)] text-[var(--al-text)] shadow"
                      : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
                  }`}
                >
                  {opt === "monthly" ? c.monthly : c.yearly}
                  {opt === "yearly" && (
                    <span className="ml-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">
                      {c.save25}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-5" data-testid="billing-plans-grid">
        {catalog.plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Crown;
          const features = lang === "pt" ? p.features_pt : p.features_en;
          const isSelected = selectedPlan === p.id;
          const isCurrent = isCurrentPlan(p.id);
          const monthly = cycle === "yearly" ? Math.round(p.monthly * 0.75) : p.monthly;
          const dailyP = (monthly / 30).toFixed(2);
          return (
            <div
              key={p.id}
              data-testid={`billing-plan-card-${p.id}`}
              className={`relative bg-[var(--al-card-bg)] border rounded-2xl p-6 transition-all flex flex-col ${
                isSelected
                  ? "border-[#C9A24E] shadow-lg shadow-[#C9A24E]/10 ring-1 ring-[#C9A24E]/30"
                  : "border-[var(--al-border)] hover:border-[#1B3A8A]/40"
              }`}
            >
              {isCurrent && (
                <span
                  data-testid={`billing-plan-current-${p.id}`}
                  className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#1B3A8A] text-white text-[10px] font-bold tracking-wide px-2.5 py-1 rounded-full"
                >
                  <Check className="w-3 h-3" /> {c.currentPlan}
                </span>
              )}
              <div className="flex items-center gap-2.5 mb-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  p.id === "growth" ? "bg-blue-100 text-blue-600"
                  : p.id === "premium" ? "bg-amber-100 text-amber-600"
                  : "bg-purple-100 text-purple-600"
                }`}>
                  <Icon className="w-4.5 h-4.5" />
                </div>
                <div className="text-[18px] font-bold text-[var(--al-text)]">{p.name}</div>
              </div>
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-[32px] font-bold text-[var(--al-text)] leading-none">${monthly}</span>
                  <span className="text-[12px] text-[var(--al-text-muted)]">{c.perSeat}</span>
                </div>
                <div className="text-[11px] text-[var(--al-text-muted)] mt-1.5">{c.perDay(dailyP)}</div>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[12.5px] text-[var(--al-text)]">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={`billing-plan-choose-${p.id}`}
                onClick={() => setSelectedPlan(p.id)}
                className={`h-11 rounded-lg text-[13px] font-bold transition-all ${
                  isSelected
                    ? "bg-[#C9A24E] text-[#0A1233] hover:bg-[#D9B25E]"
                    : "border border-[#1B3A8A] text-[#1B3A8A] hover:bg-[#1B3A8A]/5"
                }`}
              >
                {isSelected
                  ? (lang === "pt" ? "Selecionado" : "Selected")
                  : c.chooseplan}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddonsTab({ c, addons, lang, selectedAddons, toggleAddon }) {
  return (
    <div className="space-y-4" data-testid="billing-addons-list">
      <h2 className="text-[18px] font-bold text-[var(--al-text)]">{c.addons}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {addons.map((a) => {
          const Icon = ADDON_ICONS[a.id] || Sparkles;
          const status = lang === "pt" ? a.status_pt : a.status_en;
          const desc = lang === "pt" ? a.desc_pt : a.desc_en;
          const selected = selectedAddons.includes(a.id);
          return (
            <button
              type="button"
              key={a.id}
              data-testid={`billing-addon-${a.id}`}
              onClick={() => toggleAddon(a.id)}
              className={`text-left bg-[var(--al-card-bg)] border rounded-xl p-5 transition-all ${
                selected
                  ? "border-[#C9A24E] ring-1 ring-[#C9A24E]/30"
                  : "border-[var(--al-border)] hover:border-[#1B3A8A]/40"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1B3A8A]/10 text-[#1B3A8A] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[15px] font-semibold text-[var(--al-text)]">{a.name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                      selected ? "bg-emerald-100 text-emerald-700" : "bg-[var(--al-row-alt)] text-[var(--al-text-muted)]"
                    }`}>
                      {status}
                    </span>
                  </div>
                  <p className="text-[12.5px] text-[var(--al-text-muted)]">{desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BillingDetailsTab({ c, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-6" data-testid="billing-details-form">
      <Section title={c.contactInfo} icon={Building2}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="billing-name" label={c.fullName} value={value.full_name} onChange={(v) => set("full_name", v)} />
          <Input testid="billing-company" label={c.company} value={value.company} onChange={(v) => set("company", v)} />
          <Input testid="billing-email" label={c.email} value={value.email} onChange={(v) => set("email", v)} type="email" />
        </div>
      </Section>
      <Section title={c.address} icon={MapPin}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="billing-country" label={c.country} value={value.country} onChange={(v) => set("country", v)} />
          <Input testid="billing-city" label={c.city} value={value.city} onChange={(v) => set("city", v)} />
          <div className="sm:col-span-2">
            <Input testid="billing-address" label={c.address} value={value.address} onChange={(v) => set("address", v)} />
          </div>
        </div>
      </Section>
      <Section title={c.payment} icon={CreditCard}>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3">
            <Input testid="billing-card" label={c.cardNumber} value={value.card_number} onChange={(v) => set("card_number", v)} placeholder="•••• •••• •••• ••••" />
          </div>
          <Input testid="billing-exp" label={c.exp} value={value.exp} onChange={(v) => set("exp", v)} placeholder="MM / YY" />
          <Input testid="billing-cvc" label={c.cvc} value={value.cvc} onChange={(v) => set("cvc", v)} placeholder="123" />
        </div>
      </Section>
    </div>
  );
}

function ConfirmationTab({ c, plan, cycle, seats, addons, total, isYearly, onSubmit, submitting, subscription, lastInvoice }) {
  if (!plan) {
    return (
      <div className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-xl p-8 text-center text-[var(--al-text-muted)]">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }
  return (
    <div className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-2xl p-6 lg:p-8 space-y-6" data-testid="billing-confirmation">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
          <Check className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-[20px] font-bold text-[var(--al-text)]">{c.confirmTitle}</h2>
          <p className="text-[13px] text-[var(--al-text-muted)] mt-0.5">{c.confirmDesc}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-[var(--al-border)] pt-6">
        <Stat label={c.yourPlan} value={plan.name} />
        <Stat label={c.billingCycle} value={cycle === "monthly" ? c.monthly : c.yearly} />
        <Stat label={c.seatsLabel} value={seats} />
        <Stat label={c.total} value={`$${formatPrice(total)} ${isYearly ? c.perYear : c.perMonth}`} />
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-2">{c.addons}</div>
        <ul className="space-y-1.5">
          {addons.length === 0 ? (
            <li className="text-[12px] text-[var(--al-text-muted)] italic">—</li>
          ) : addons.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-[13px]">
              <Check className="w-3.5 h-3.5 text-emerald-500" /> {a.name}
            </li>
          ))}
        </ul>
      </div>

      {subscription?.status === "active" && (
        <div className="text-[12px] text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg">
          ✓ {c.activated}
        </div>
      )}

      {lastInvoice && (
        <div
          data-testid="billing-invoice-card"
          className="bg-[var(--al-bg)] border border-[var(--al-border)] rounded-xl p-4 flex items-center justify-between gap-4"
        >
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)]">Invoice</div>
            <div className="text-[15px] font-semibold text-[var(--al-text)] mt-0.5">{lastInvoice.invoice_number}</div>
            <div className="text-[12px] text-[var(--al-text-muted)] mt-0.5">
              ${Number(lastInvoice.total || 0).toLocaleString()} {lastInvoice.currency || "USD"} · {lastInvoice.status}
            </div>
          </div>
          <a
            href={`${API}/billing/invoices/${lastInvoice.id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="billing-invoice-download"
            className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#0A1233] hover:bg-[#1B3A8A] text-white text-[12.5px] font-semibold"
          >
            PDF
          </a>
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        data-testid="billing-confirm-submit"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] font-bold text-[14px] disabled:opacity-60"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {c.confirmCta}
      </button>
    </div>
  );
}

function OrderSummary({ c, plan, addons, seats, cycle, unit, total, lang, subscription, onContinueTrial, onContinue, submitting }) {
  const isYearly = cycle === "yearly";
  return (
    <div
      data-testid="billing-order-summary"
      className="lg:sticky lg:top-24 bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-[15px] font-bold text-[var(--al-text)]">{c.orderSummary}</h3>
        <span className="text-[10px] font-bold text-[#C9A24E] bg-[#C9A24E]/10 px-2 py-0.5 rounded">
          {c.trial14}
        </span>
      </div>
      {plan ? (
        <>
          <div className="border-t border-[var(--al-border)] mt-4 pt-4 space-y-3 text-[13px]">
            <Row label={c.yourPlan} value={plan.name} />
            <Row label={c.billingCycle} value={cycle === "monthly" ? c.monthly : c.yearly} />
            <Row label={c.seatsLabel} value={`${seats} × $${unit}`} />
          </div>

          <div className="border-t border-[var(--al-border)] mt-4 pt-4">
            <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-2">{c.addons}</div>
            <ul className="space-y-1.5">
              {addons.length === 0 ? (
                <li className="text-[12.5px] text-[var(--al-text-muted)] italic">—</li>
              ) : addons.map((a) => (
                <li key={a.id} className="flex items-center justify-between text-[13px]">
                  <span>{a.name}</span>
                  <span className="text-emerald-600 text-[11px] font-semibold">
                    {lang === "pt" ? a.status_pt : a.status_en}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-[var(--al-border)] mt-4 pt-4 flex items-baseline justify-between">
            <span className="text-[12px] uppercase tracking-wide text-[var(--al-text-muted)]">{c.total}</span>
            <span className="text-[24px] font-bold text-[var(--al-text)]">
              ${formatPrice(total)}
              <span className="text-[12px] text-[var(--al-text-muted)] font-normal ml-1">
                {isYearly ? c.perYear : c.perMonth}
              </span>
            </span>
          </div>

          <div className="mt-5 space-y-2">
            <button
              type="button"
              onClick={onContinueTrial}
              data-testid="billing-summary-trial"
              className="w-full h-11 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white font-bold text-[13px]"
            >
              {c.continueTrial}
            </button>
            <button
              type="button"
              onClick={onContinue}
              disabled={submitting}
              data-testid="billing-summary-continue"
              className="w-full h-11 rounded-lg border border-[#1B3A8A] text-[#1B3A8A] hover:bg-[#1B3A8A]/5 font-bold text-[13px] disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {c.continue}
            </button>
          </div>
        </>
      ) : (
        <div className="py-12 text-center text-[var(--al-text-muted)]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-xl p-5 lg:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#1B3A8A]" />
        <h3 className="text-[14px] font-bold text-[var(--al-text)]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-1.5 block">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full h-10 px-3 rounded-lg bg-[var(--al-bg)] border border-[var(--al-border)] focus:border-[#1B3A8A] focus:bg-[var(--al-card-bg)] outline-none text-[13px] text-[var(--al-text)] transition-colors"
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--al-text-muted)]">{label}</span>
      <span className="font-semibold text-[var(--al-text)]">{value}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--al-text-muted)]">{label}</div>
      <div className="text-[15px] font-semibold text-[var(--al-text)] mt-1">{value}</div>
    </div>
  );
}
